import ExponentTool from "../MathTools/ExponentTool"; // optional
import SystemSolverTool from "../MathTools/SystemSolverTool"; // optional
import QuadraticGraphTool from "../MathTools/QuadraticGraphTool"; // optional
import InteractiveAreaModel from "../MathTools/InteractiveAreaModel";

export const chapterPages = {
    // -----------------------------------------------------
    // CHAPTER 1: Exponents & Radicals
    // -----------------------------------------------------
    1: [
      {
        title: <h1>Exponents & Radicals — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>Operations with Powers (Integral Exponents).</strong> In Grade 10, you review
              the laws of exponents for integral exponents (positive, negative, and zero). We can
              simplify expressions like <code>a<sup>m</sup> ⋅ a<sup>n</sup> = a<sup>m+n</sup></code> and 
              <code>a<sup>n</sup> / a<sup>m</sup> = a<sup>n−m</sup></code>. Zero exponents give a
              value of 1, and negative exponents represent reciprocals.
            </p>
            <p>
              Understanding these exponent rules is essential for simplifying expressions and
              evaluating large or small powers. You’ll also see negative exponents in contexts like
              scientific notation.
            </p>
          </>
        )
      },
      {
        title: <h1>Exponents & Radicals — Slide 2</h1>,
        body: (
          <>
            <p>
              <strong>Prime Factorization & Radical Review.</strong> Prime factorization helps us
              simplify square roots (radicals). For example, 
              <code>√360 = √(2³ × 3² × 5) = 6√10</code>. Recognizing perfect square factors is
              the key.  
            </p>
            <p>
              Also, fractional exponents connect directly to radicals: 
              <code>a<sup>1/2</sup> = √a</code>, <code>a<sup>1/3</sup> = ³√a</code>. This allows
              moving back and forth between radical form and exponent form.
            </p>
          </>
        )
      },
      {
        title: <h1>Exponents & Radicals — Slide 3</h1>,
        body: (
          <>
            <p>
              <strong>Order of Operations with Exponents.</strong> Always remember to handle
              exponents before multiplication/division when following BEDMAS/PEDMAS. This is
              especially important when evaluating expressions that mix powers, roots, and
              arithmetic.
            </p>
          </>
        )
      },
      {
        title: <h1>Exponents & Radicals — Slide 4</h1>,
        body: (
          <>
            <p>
              <em>Try the (optional) ExponentTool below</em> if you want interactive practice
              with exponent expressions:
            </p>
            <ExponentTool />
          </>
        )
      },
    ],
  
    // -----------------------------------------------------
    // CHAPTER 2: Polynomials (Operations)
    // -----------------------------------------------------
    2: [
      {
        title: <h1>Polynomials (Operations) — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>Polynomial Basics.</strong> A polynomial is an expression consisting of
              terms that are added or subtracted. Each term is a product of a coefficient and
              variables raised to exponents. For example, 
              <code>3x² + 5x − 2</code>.
            </p>
          </>
        )
      },
      {
        title: <h1>Polynomials (Operations) — Slide 2</h1>,
        body: (
          <>
            <p>
              <strong>Adding & Subtracting Polynomials.</strong> Combine like terms (same variable
              and exponent) to simplify. Example:
            </p>
            <pre style={{background:"#f5f5f5",padding:8}}>
  (3x² + 5x - 2) + (4x² - 3x + 6) = 7x² + 2x + 4
            </pre>
          </>
        )
      },
      {
        title: <h1>Polynomials (Operations) — Slide 3</h1>,
        body: (
          <>
            <p>
              <strong>Multiplying Polynomials.</strong> Use the distributive property. Multiply
              each term of the first polynomial by each term of the second. For binomial×binomial,
              you might use FOIL (First, Outer, Inner, Last). For example:
            </p>
            <pre style={{background:"#f5f5f5",padding:8}}>
  (x + 3)(x - 2) = x·x + x·(-2) + 3·x + 3·(-2) = x² + x - 6
            </pre>
          </>
        )
      },
      {
          title: <h1>Polynomials (Operations) — Slide 4</h1>,
          body: (
            <>
              <p><strong>Interactive Area Model.</strong> Here’s our own visualization …</p>
              <InteractiveAreaModel scale={2.5} />
              <p style={{ marginTop: "0.5em" }}>
                Notice how … sums to <code>x² + x − 6</code>.
              </p>
            </>
          )
        },
    ],
  
    // -----------------------------------------------------
    // CHAPTER 3: Polynomial Factoring
    // -----------------------------------------------------
    3: [
      {
        title: <h1>Polynomial Factoring — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>Common Factor (GCF) Factoring.</strong> Always start by checking for a
              greatest common factor in all terms. E.g., 
              <code>6x² - 9x = 3x(2x - 3)</code>.
            </p>
          </>
        )
      },
      {
        title: <h1>Polynomial Factoring — Slide 2</h1>,
        body: (
          <>
            <p>
              <strong>Factoring Trinomials (a=1).</strong> If you have 
              <code>x² + bx + c</code>, find two numbers that multiply to <code>c</code> and
              add to <code>b</code>. Example: 
              <code>x² + 5x + 6 = (x+2)(x+3)</code>.
            </p>
          </>
        )
      },
      {
        title: <h1>Polynomial Factoring — Slide 3</h1>,
        body: (
          <>
            <p>
              <strong>Factoring Trinomials (a≠1).</strong> For something like 
              <code>2x² + 7x + 3</code>, you can use decomposition or trial-and-error to find
              factors <code>(2x + 1)(x + 3)</code>.
            </p>
          </>
        )
      },
      {
        title: <h1>Polynomial Factoring — Slide 4</h1>,
        body: (
          <>
            <p>
              <strong>Special Forms.</strong> Recognize <em>difference of squares</em>: 
              <code>a² - b² = (a+b)(a-b)</code>. Perfect square trinomials are another pattern
              you might see: <code>a² ± 2ab + b² = (a ± b)²</code> (though mostly extended
              practice).
            </p>
          </>
        )
      },
      {
          title: <h1>Polynomial Factoring — Slide 5</h1>,
          body: (
            <>
              <p>
                <strong>Solving by Factoring.</strong> While more common in Grade 11, you might see
                simpler quadratics in Grade 10 that factor nicely.  Factoring lets you find the
                x‑intercepts (roots) of the graph quickly.
              </p>
              <p>
                Try the interactive graph below to see how the factors of <code>x² + 5x + 6</code>
                correspond to the roots at <code>x=–2</code> and <code>x=–3</code>.
              </p>
        
              {/* Illustrate: graph of y = x^2 + 5x + 6 with its roots marked */}
              <QuadraticGraphTool
                equation="x^2 + 5x + 6"
                showRoots={true}
                style={{ width: "100%", maxWidth: 500, margin: "1em auto" }}
              />
        
              <p>
                Notice that when you factor <code>x² + 5x + 6</code> into <code>(x+2)(x+3)</code>,
                you immediately read off the zeros at <code>–2</code> and <code>–3</code>.
              </p>
            </>
          )
        }
    ],
  
    // -----------------------------------------------------
    // CHAPTER 4: Relations & Functions
    // -----------------------------------------------------
    4: [
      {
          title: <h1>Relations & Functions — Slide 1</h1>,
          body: (
            <>
              <p>
                <strong>What is a Function?</strong> 
                A function is a special relation where each input has exactly one output.
              </p>
              <p>
                Graphically, you can apply the <strong>vertical line test</strong>: 
                if any vertical line intersects the curve or set of points more than once, it’s <em>not</em> a function.
              </p>
        
              {/* two tiny plots, side by side */}
              <div style={{ display: "flex", justifyContent: "center", gap: "1em", marginTop: "1em" }}>
                {/* valid function */}
                <svg width={150} height={150} style={{ border: "1px solid #ccc" }}>
                  {/* axes */}
                  <line x1={10} y1={140} x2={140} y2={140} stroke="#666" />
                  <line x1={10} y1={10}  x2={10}  y2={140} stroke="#666" />
        
                  {/* points: (1,2),(2,4),(3,6) scaled by 20 */}
                  {[ [1,2], [2,4], [3,6] ].map(([x,y],i) => (
                    <circle key={i} cx={10 + x*30} cy={140 - y*20} r={4} fill="#0074D9" />
                  ))}
        
                  {/* vertical line at x=2 */}
                  <line x1={10 + 2*30} y1={10} x2={10 + 2*30} y2={140} stroke="#FF4136" strokeDasharray="4" />
                  <text x={60} y={15} fontSize={12} fill="#333">Function ✔️</text>
                </svg>
        
                {/* not a function */}
                <svg width={150} height={150} style={{ border: "1px solid #ccc" }}>
                  {/* axes */}
                  <line x1={10} y1={140} x2={140} y2={140} stroke="#666" />
                  <line x1={10} y1={10}  x2={10}  y2={140} stroke="#666" />
        
                  {/* points: (1,2),(1,3),(2,5) */}
                  {[ [1,2], [1,3], [2,5] ].map(([x,y],i) => (
                    <circle key={i} cx={10 + x*30} cy={140 - y*20} r={4} fill="#2ECC40" />
                  ))}
        
                  {/* vertical line at x=1 */}
                  <line x1={10 + 1*30} y1={10} x2={10 + 1*30} y2={140} stroke="#FF4136" strokeDasharray="4" />
                  <text x={60} y={15} fontSize={12} fill="#333">Not a Function ❌</text>
                </svg>
              </div>
            </>
          )
        },
        {
          title: <h1>Relations & Functions — Slide 2</h1>,
          body: (
            <>
              <p>
                <strong>Connecting Representations.</strong> Move between real‑life situations,
                tables, and graphs. Here’s a plant’s height over time recorded in a table and
                then plotted. Each time (day) has exactly one height — so it’s a function.
              </p>
        
              <div style={{ display: "flex", justifyContent: "center", gap: "2em", marginTop: "1em" }}>
                {/* data table */}
                <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid #999", padding: "4px 8px" }}>Day</th>
                      <th style={{ border: "1px solid #999", padding: "4px 8px" }}>Height (cm)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[ [1,2], [2,5], [3,9], [4,14] ].map(([day, h], i) => (
                      <tr key={i}>
                        <td style={{ border: "1px solid #999", textAlign: "center", padding: "4px 8px" }}>{day}</td>
                        <td style={{ border: "1px solid #999", textAlign: "center", padding: "4px 8px" }}>{h}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
        
                {/* plotted points */}
                <svg width={220} height={160} style={{ border: "1px solid #ccc" }}>
                  {/* axes */}
                  <line x1={40} y1={140} x2={200} y2={140} stroke="#666" />
                  <line x1={40} y1={20}  x2={40}  y2={140} stroke="#666" />
                  {/* axis labels */}
                  <text x={100} y={155} fontSize={10} fill="#333">Time (days)</text>
                  <text x={5} y={20}   fontSize={10} fill="#333">Height (cm)</text>
        
                  {/* points (day, height) scaled: x→×40px, y→×7px */}
                  {[ [1,2], [2,5], [3,9], [4,14] ].map(([day, h], i) => (
                    <circle
                      key={i}
                      cx={40 + day * 40}
                      cy={140 - h * 7}
                      r={4}
                      fill="#0074D9"
                    />
                  ))}
        
                  {/* optional lines connecting them */}
                  <polyline
                    points={ [ [1,2],[2,5],[3,9],[4,14] ]
                      .map(([d,h]) => `${40 + d*40},${140 - h*7}`)
                      .join(" ") }
                    fill="none"
                    stroke="#0074D9"
                    strokeDasharray="4"
                  />
                </svg>
              </div>
        
              <p style={{ marginTop: "0.5em" }}>
                Because each day maps to exactly one height point on the graph, this relation
                passes the function test.
              </p>
            </>
          )
        },
        {
          title: <h1>Relations & Functions — Slide 3</h1>,
          body: (
            <>
              <p>
                <strong>Domain &amp; Range.</strong> The <em>domain</em> is the set of all possible inputs
                (x‑values), and the <em>range</em> is the set of all possible outputs (y‑values). Here’s
                how they differ when you have discrete data vs. a continuous function.
              </p>
        
              <div style={{ display: "flex", justifyContent: "center", gap: "3em", marginTop: "1em" }}>
                {/* DISCRETE DOMAIN */}
                <div>
                  <p style={{ textAlign: "center", margin: 0 }}><em>Discrete Domain</em></p>
                  <svg width={200} height={60} style={{ border: "1px solid #ddd" }}>
                    {/* horizontal axis */}
                    <line x1={20} y1={40} x2={180} y2={40} stroke="#666" />
                    {/* discrete input points at x = 1,2,3,4 */}
                    {[1, 2, 3, 4].map((d) => (
                      <circle
                        key={d}
                        cx={20 + d * 40}
                        cy={40}
                        r={5}
                        fill="#0074D9"
                      />
                    ))}
                    {/* tick labels */}
                    {[1,2,3,4].map((d) => (
                      <text
                        key={d}
                        x={20 + d * 40}
                        y={55}
                        fontSize={10}
                        textAnchor="middle"
                        fill="#333"
                      >
                        {d}
                      </text>
                    ))}
                  </svg>
                  <p style={{ fontSize: 12, textAlign: "center", marginTop: "0.3em" }}>
                    Domain: {"{1, 2, 3, 4}"}<br/>
                    Range: analogous discrete set
                  </p>
                </div>
        
                {/* CONTINUOUS DOMAIN */}
                <div>
                  <p style={{ textAlign: "center", margin: 0 }}><em>Continuous Domain</em></p>
                  <svg width={200} height={60} style={{ border: "1px solid #ddd" }}>
                    <defs>
                      <marker
                        id="arrow"
                        markerWidth="6"
                        markerHeight="6"
                        refX="5"
                        refY="3"
                        orient="auto"
                      >
                        <path d="M0,0 L6,3 L0,6 Z" fill="#666" />
                      </marker>
                    </defs>
                    {/* continuous axis with arrows */}
                    <line
                      x1={20}
                      y1={40}
                      x2={180}
                      y2={40}
                      stroke="#0074D9"
                      strokeWidth={2}
                      markerStart="url(#arrow)"
                      markerEnd="url(#arrow)"
                    />
                    {/* endpoints labels */}
                    <text x={20} y={55} fontSize={10} textAnchor="middle" fill="#333">
                      0
                    </text>
                    <text x={180} y={55} fontSize={10} textAnchor="middle" fill="#333">
                      10
                    </text>
                  </svg>
                  <p style={{ fontSize: 12, textAlign: "center", marginTop: "0.3em" }}>
                    Domain: all real x ≥ 0<br/>
                    Range: continuous interval on y‑axis
                  </p>
                </div>
              </div>
        
              <p style={{ marginTop: "0.5em" }}>
                In both cases you could do the same on the vertical axis to see the <strong>range</strong>:
                discrete outputs vs. a continuous interval.
              </p>
            </>
          )
        },
        {
          title: <h1>Relations & Functions — Slide 4</h1>,
          body: (
            <>
              <p>
                <strong>Function Notation.</strong> <code>f(x)</code> denotes the value of the
                function <em>f</em> at the input <code>x</code>. For example,
                <code>f(a) = b</code> means “when <code>x = a</code>, then the output is
                <code>b</code>.”
              </p>
        
              {/* Input–Output Machine Illustration */}
              <div style={{ textAlign: "center", margin: "1.5em 0" }}>
                <svg width={380} height={120}>
                  <defs>
                    <marker
                      id="arrowhead"
                      markerWidth="6"
                      markerHeight="6"
                      refX="6"
                      refY="3"
                      orient="auto"
                    >
                      <path d="M0,0 L6,3 L0,6 Z" fill="#555" />
                    </marker>
                  </defs>
        
                  {/* Input line with arrow */}
                  <line
                    x1={20}
                    y1={70}
                    x2={140}
                    y2={70}
                    stroke="#555"
                    strokeWidth={2}
                    markerEnd="url(#arrowhead)"
                  />
                  <text x={20} y={60} fontSize={14} fill="#333">
                    x
                  </text>
        
                  {/* Function “machine” box */}
                  <rect
                    x={140}
                    y={40}
                    width={120}
                    height={60}
                    rx={8}
                    fill="#e8f5e9"
                    stroke="#388e3c"
                    strokeWidth={2}
                  />
                  <text
                    x={200}
                    y={75}
                    fontSize={18}
                    fill="#2e7d32"
                    textAnchor="middle"
                  >
                    f
                    <tspan fontSize={12} dy={-2}>
                      (x)
                    </tspan>
                  </text>
        
                  {/* Output line with arrow */}
                  <line
                    x1={260}
                    y1={70}
                    x2={360}
                    y2={70}
                    stroke="#555"
                    strokeWidth={2}
                    markerEnd="url(#arrowhead)"
                  />
                  <text
                    x={360}
                    y={60}
                    fontSize={14}
                    fill="#333"
                    textAnchor="end"
                  >
                    f(x)
                  </text>
                </svg>
              </div>
        
              <p style={{ marginTop: "0.5em" }}>
                Think of <code>f</code> as a “machine” that takes an <strong>input</strong> (<code>x</code>)
                on the left, applies its rule <code>f(x)</code> inside the box, and spits out the
                <strong>output</strong> (<code>f(x)</code>) on the right.
              </p>
              <p>
              That’s an overview of relations and functions. On to linear functions next!
            </p>
            </>
          )
        },
    ],
  
    // -----------------------------------------------------
    // CHAPTER 5: Linear Functions (Slope & Equations)
    // -----------------------------------------------------
    5: [
      {
          title: <h1>Linear Functions — Slide 1</h1>,
          body: (
            <>
              <p>
                <strong>Slope (Rate of Change).</strong> For a linear function, slope is the constant
                rate of change. If <code>m</code> is the slope, then <code>y = mx + b</code> has a rise
                of <code>m</code> for every 1 unit of run. We calculate slope by
                <code>m = (y₂ − y₁)/(x₂ − x₁)</code>.
              </p>
        
              <div style={{ textAlign: "center", margin: "1em 0" }}>
                <svg width={200} height={200} style={{ border: "1px solid #ccc" }}>
                  {/* axes */}
                  <line x1={20} y1={180} x2={180} y2={180} stroke="#555" />
                  <line x1={20} y1={180} x2={20} y2={20} stroke="#555" />
        
                  {/* the line through (40,140) and (160,60) */}
                  <line x1={40} y1={140} x2={160} y2={60} stroke="#0074D9" strokeWidth={2} />
        
                  {/* the two points */}
                  <circle cx={40} cy={140} r={4} fill="#FF4136" />
                  <circle cx={160} cy={60} r={4} fill="#FF4136" />
        
                  {/* rise/run right triangle */}
                  <polyline
                    points="40,140 40,60 160,60"
                    fill="none"
                    stroke="#FF851B"
                    strokeDasharray="4"
                  />
        
                  {/* labels */}
                  <text x={42} y={100} fontSize={12} fill="#FF851B">rise = y₂ − y₁</text>
                  <text x={100} y={58} fontSize={12} fill="#FF851B">run = x₂ − x₁</text>
                </svg>
                <p style={{ fontSize: "0.9em", marginTop: "0.5em" }}>
                  <em>The slope is the “rise” over the “run,”  
                  here (60 − 140)/(160 − 40) = −80/120 = −2/3.</em>
                </p>
              </div>
            </>
          )
        },
        {
          title: <h1>Linear Functions — Slide 2</h1>,
          body: (
            <>
              <p>
                <strong>Slope–Intercept Form.</strong> The form <code>y = mx + b</code> is usually
                easiest to graph quickly. <code>m</code> is the slope, and <code>b</code> is the
                y‑intercept. We can also see point‑slope form or standard form.
              </p>
        
              <div style={{ textAlign: "center", margin: "1em 0" }}>
                <svg width={220} height={220} style={{ border: "1px solid #ccc" }}>
                  {/* axes */}
                  <line x1={30} y1={190} x2={190} y2={190} stroke="#555" />
                  <line x1={30} y1={190} x2={30} y2={30} stroke="#555" />
        
                  {/* y-intercept at (0, b) */}
                  <circle cx={30} cy={110} r={4} fill="#2ECC40" />
                  <text x={35} y={110} fontSize={12} fill="#2ECC40">b</text>
        
                  {/* slope steps: rise/run */}
                  {/* let's pick m=2/3 => rise=−40px, run=60px (but upward for positive slope) */}
                  <polyline
                    points="30,110 30,50 90,50"
                    fill="none"
                    stroke="#FF851B"
                    strokeWidth={2}
                    markerEnd="url(#arrow)"
                  />
                  <defs>
                    <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3">
                      <path d="M0,0 L6,3 L0,6 Z" fill="#FF851B" />
                    </marker>
                  </defs>
                  <text x={32} y={80} fontSize={12} fill="#FF851B">rise</text>
                  <text x={60} y={48} fontSize={12} fill="#FF851B">run</text>
        
                  {/* the actual line */}
                  <line x1={30} y1={110} x2={190} y2={30} stroke="#0074D9" strokeWidth={2} />
        
                  {/* tick marks */}
                  {[50, 90, 130, 170].map(x => (
                    <line key={x} x1={x} y1={190} x2={x} y2={185} stroke="#555" />
                  ))}
                  {[50, 90, 130, 170].map(y => (
                    <line key={y} x1={30} y1={y} x2={35} y2={y} stroke="#555" />
                  ))}
                </svg>
                <p style={{ fontSize: "0.9em", marginTop: "0.5em" }}>
                  <em>Start at (0, b), then move “rise/run” (here +60px right, −60px up) to plot the line.</em>
                </p>
              </div>
            </>
          )
        },
        {
          title: <h1>Linear Functions — Slide 3</h1>,
          body: (
            <>
              <p>
                <strong>Graphing Methods.</strong> You can graph a line by first plotting the
                y‑intercept, then applying the slope (rise/run) repeatedly to get more points.
                Alternatively, fill out a table of values and plot each (x,y). In real-world
                contexts, slope often represents a rate (e.g. speed, cost per item, etc.).
              </p>
        
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "flex-start",
                  margin: "1em 0"
                }}
              >
                {/* Value Table */}
                <table
                  style={{
                    borderCollapse: "collapse",
                    textAlign: "center",
                    marginRight: "2em"
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid #aaa", padding: "4px 8px" }}>x</th>
                      <th style={{ border: "1px solid #aaa", padding: "4px 8px" }}>y = x + 2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[0, 1, 2].map((x) => (
                      <tr key={x}>
                        <td style={{ border: "1px solid #aaa", padding: "4px 8px" }}>{x}</td>
                        <td style={{ border: "1px solid #aaa", padding: "4px 8px" }}>
                          {x + 2}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
        
                {/* Graph */}
                <svg width={220} height={220} style={{ border: "1px solid #ccc" }}>
                  {/* axes */}
                  <line x1={30} y1={190} x2={190} y2={190} stroke="#555" />
                  <line x1={30} y1={190} x2={30} y2={30} stroke="#555" />
        
                  {/* plotted points */}
                  {[
                    { x: 0, y: 2 },
                    { x: 1, y: 3 },
                    { x: 2, y: 4 }
                  ].map((pt, i) => {
                    const px = 30 + pt.x * 50;
                    const py = 190 - pt.y * 40;
                    return (
                      <circle key={i} cx={px} cy={py} r={4} fill="#0074D9" />
                    );
                  })}
        
                  {/* connecting line */}
                  <line
                    x1={30 + 0 * 50}
                    y1={190 - 2 * 40}
                    x2={30 + 2 * 50}
                    y2={190 - 4 * 40}
                    stroke="#0074D9"
                    strokeWidth={2}
                  />
        
                  {/* tick marks */}
                  {[0, 1, 2, 3].map((v) => (
                    <line
                      key={`xt${v}`}
                      x1={30 + v * 50}
                      y1={190}
                      x2={30 + v * 50}
                      y2={185}
                      stroke="#555"
                    />
                  ))}
                  {[2, 3, 4].map((v) => (
                    <line
                      key={`yt${v}`}
                      x1={30}
                      y1={190 - v * 40}
                      x2={35}
                      y2={190 - v * 40}
                      stroke="#555"
                    />
                  ))}
                </svg>
              </div>
        
              <p style={{ fontSize: "0.9em", fontStyle: "italic" }}>
                Use the table to plot each (x,y) and then connect them to form the line.
              </p>
            </>
          )
        },
        {
          title: <h1>Linear Functions — Slide 4</h1>,
          body: (
            <>
              <p>
                <strong>Parallel &amp; Perpendicular Lines.</strong> Two lines are
                <em>parallel</em> if they have the same slope, and
                <em>perpendicular</em> if their slopes are negative reciprocals.
                Below are two mini‑graphs illustrating each.
              </p>
        
              <div style={{ display: "flex", justifyContent: "space-around", marginTop: "1em" }}>
                {/* Parallel Lines */}
                <div style={{ textAlign: "center" }}>
                  <p style={{ margin: "0 0 0.5em 0" }}>Parallel: <code>y=2x+1</code> &amp; <code>y=2x−2</code></p>
                  <svg width={200} height={160} style={{ border: "1px solid #ccc" }}>
                    {/* axes */}
                    <line x1={20} y1={140} x2={180} y2={140} stroke="#555" />
                    <line x1={20} y1={140} x2={20} y2={20} stroke="#555" />
        
                    {/* y=2x+1 */}
                    <line
                      x1={20}
                      y1={140 - (2 * 0 + 1) * 20}
                      x2={180}
                      y2={140 - (2 * 8 + 1) * 20}
                      stroke="#0074D9"
                      strokeWidth={2}
                    />
        
                    {/* y=2x-2 */}
                    <line
                      x1={20}
                      y1={140 - (2 * 0 - 2) * 20}
                      x2={180}
                      y2={140 - (2 * 8 - 2) * 20}
                      stroke="#FF4136"
                      strokeWidth={2}
                    />
                  </svg>
                </div>
        
                {/* Perpendicular Lines */}
                <div style={{ textAlign: "center" }}>
                  <p style={{ margin: "0 0 0.5em 0" }}>Perpendicular: <code>y=2x+1</code> &amp; <code>y=−½x+2</code></p>
                  <svg width={200} height={160} style={{ border: "1px solid #ccc" }}>
                    {/* axes */}
                    <line x1={20} y1={140} x2={180} y2={140} stroke="#555" />
                    <line x1={20} y1={140} x2={20} y2={20} stroke="#555" />
        
                    {/* y=2x+1 */}
                    <line
                      x1={20}
                      y1={140 - (2 * 0 + 1) * 20}
                      x2={180}
                      y2={140 - (2 * 8 + 1) * 20}
                      stroke="#0074D9"
                      strokeWidth={2}
                    />
        
                    {/* y=-0.5x+2 */}
                    <line
                      x1={20}
                      y1={140 - (-0.5 * 0 + 2) * 20}
                      x2={180}
                      y2={140 - (-0.5 * 8 + 2) * 20}
                      stroke="#2ECC40"
                      strokeWidth={2}
                    />
                  </svg>
                </div>
              </div>
        
              <p style={{ marginTop: "0.8em", fontSize: "0.9em", fontStyle: "italic" }}>
                Notice in the left graph both lines “rise 2 for every 1 run” (same slope). In the right,
                one line’s slope is 2 and the other is −½, so they intersect at a right angle.
              </p>
            </>
          )
        },
    ],
  
    // -----------------------------------------------------
    // CHAPTER 6: Arithmetic Sequences
    // -----------------------------------------------------
    6: [
      {
          title: <h1>Arithmetic Sequences — Slide 1</h1>,
          body: (
            <>
              <p>
                <strong>Definition.</strong> An arithmetic sequence increases (or decreases) by the
                same amount <code>d</code> each time. Example: 4, 7, 10, 13, … has common difference <code>+3</code>.
              </p>
        
              {/* Illustration: a number‐line showing each jump of +3 */}
              <div style={{ textAlign: "center", margin: "1em 0" }}>
                <svg width="400" height="120" style={{ border: "1px solid #ccc" }}>
                  {/* Draw the baseline */}
                  <line x1="20" y1="60" x2="380" y2="60" stroke="#555" />
        
                  {/* Render each term as a dot + label, and arrows between them */}
                  {(() => {
                    const terms = [4, 7, 10, 13];
                    const d = 3;
                    const spacing = 100; // px between consecutive terms
                    return terms.map((val, i) => {
                      const x = 20 + i * spacing;
                      return (
                        <g key={i}>
                          {/* dot */}
                          <circle cx={x} cy={60} r={6} fill="#4caf50" />
                          {/* label */}
                          <text x={x - 10} y={90} fontSize={14} fill="#333">
                            {val}
                          </text>
                          {/* arrow to next */}
                          {i < terms.length - 1 && (
                            <>
                              <line
                                x1={x + 6}
                                y1={60}
                                x2={x + spacing - 6}
                                y2={60}
                                stroke="#4caf50"
                                markerEnd="url(#arrowhead)"
                              />
                              {/* +d label */}
                              <text
                                x={x + spacing / 2 - 10}
                                y={40}
                                fontSize={12}
                                fill="#4caf50"
                              >
                                +{d}
                              </text>
                            </>
                          )}
                        </g>
                      );
                    });
                  })()}
        
                  {/* define an arrowhead marker once */}
                  <defs>
                    <marker
                      id="arrowhead"
                      markerWidth="10"
                      markerHeight="7"
                      refX="10"
                      refY="3.5"
                      orient="auto"
                    >
                      <polygon points="0 0, 10 3.5, 0 7" fill="#4caf50" />
                    </marker>
                  </defs>
                </svg>
              </div>
            </>
          )
        },
        {
          title: <h1>Arithmetic Sequences — Slide 2</h1>,
          body: (
            <>
              <p>
                <strong>Explicit Formula.</strong> The nth term <code>tₙ</code> is given by 
                <code>tₙ = a + (n−1)d</code>, where <code>a</code> is the first term and 
                <code>d</code> is the common difference.
              </p>
              <p>
                This is very similar to the form of a linear function. <code>n</code> is the
                independent variable, <code>tₙ</code> is the dependent variable.
              </p>
              <div style={{ textAlign: "center", margin: "1em 0" }}>
                <svg width="240" height="200" style={{ border: "1px solid #ccc" }}>
                  {/* axes */}
                  <line x1={40} y1={10} x2={40} y2={160} stroke="#333" />
                  <line x1={40} y1={160} x2={200} y2={160} stroke="#333" />
                  {/* sequence points & connecting line: a=2, d=3 => points (1,2),(2,5),(3,8),(4,11) */}
                  <polyline
                    fill="none"
                    stroke="#4caf50"
                    strokeWidth={2}
                    points="40,140 80,110 120,80 160,50"
                  />
                  {[{n:1,t:2},{n:2,t:5},{n:3,t:8},{n:4,t:11}].map((p) => (
                    <g key={p.n}>
                      <circle
                        cx={40 + (p.n - 1) * 40}
                        cy={160 - p.t * 10}
                        r={4}
                        fill="#4caf50"
                      />
                      <text
                        x={40 + (p.n - 1) * 40 - 10}
                        y={165 - p.t * 10}
                        fontSize={12}
                        fill="#333"
                      >
                        ({p.n},{p.t})
                      </text>
                    </g>
                  ))}
                  {/* axis labels */}
                  <text x={200} y={175} fontSize={12} fill="#333">n</text>
                  <text x={5} y={10} fontSize={12} fill="#333">tₙ</text>
                </svg>
              </div>
            </>
          )
        },
        {
          title: <h1>Arithmetic Sequences — Slide 3</h1>,
          body: (
            <>
              <p>
                <strong>Graphing Sequences.</strong> If we plot the terms (vertical) vs. <code>n</code>
                (horizontal), we get discrete points forming a line (if we connected them).
              </p>
              <div style={{ textAlign: "center", margin: "1em 0" }}>
                <svg width={240} height={180} style={{ border: "1px solid #ccc" }}>
                  {/* axes */}
                  <line x1={40} y1={10} x2={40} y2={150} stroke="#333" />
                  <line x1={40} y1={150} x2={230} y2={150} stroke="#333" />
                  {/* tick marks and labels */}
                  {[1,2,3,4,5].map(n => (
                    <g key={n}>
                      <line
                        x1={40 + (n * 36)}
                        y1={145}
                        x2={40 + (n * 36)}
                        y2={155}
                        stroke="#333"
                      />
                      <text
                        x={40 + (n * 36)}
                        y={168}
                        fontSize={12}
                        textAnchor="middle"
                      >
                        {n}
                      </text>
                    </g>
                  ))}
                  {[2,5,8,11].map((t,i) => (
                    <g key={i}>
                      <line
                        x1={35}
                        y1={150 - (t * 10 / 1)}
                        x2={45}
                        y2={150 - (t * 10 / 1)}
                        stroke="#333"
                      />
                      <text x={20} y={150 - (t * 10 / 1) + 4} fontSize={12} textAnchor="end">
                        {t}
                      </text>
                    </g>
                  ))}
                  {/* points for tₙ = 2 + (n−1)*3 */}
                  {[1,2,3,4].map((n,i) => {
                    const t = 2 + (n - 1) * 3;
                    return (
                      <circle
                        key={i}
                        cx={40 + (n * 36)}
                        cy={150 - (t * 10 / 1)}
                        r={5}
                        fill="#1976d2"
                      />
                    );
                  })}
                </svg>
              </div>
              <p style={{ fontStyle: "italic", fontSize: 26 }}>
                Sample sequence plotted: tₙ = 2 + (n−1)·3 → (1,2),(2,5),(3,8),(4,11)
              </p>
            </>
          )
        },
        {
          title: <h1>Arithmetic Sequences — Slide 4</h1>,
          body: (
            <>
              <p>
                <strong>Applications.</strong> Many real patterns (like a growing arrangement of blocks)
                can be arithmetic. Below is an example where each row has one more block than the last.
              </p>
              <div style={{ textAlign: "center", margin: "1em 0" }}>
                <svg width={250} height={140} style={{ border: "1px solid #ccc" }}>
                  {/* Draw 4 rows of blocks: row 1 has 1, row 2 has 2, etc. */}
                  {[1, 2, 3, 4].map((count, row) =>
                    Array.from({ length: count }).map((_, col) => (
                      <rect
                        key={`block-${row}-${col}`}
                        x={20 + col * 30}
                        y={20 + row * 30}
                        width={25}
                        height={25}
                        fill="#90caf9"
                        stroke="#1e88e5"
                      />
                    ))
                  )}
                  {/* Label each row’s block count */}
                  {[1, 2, 3, 4].map((count, row) => (
                    <text
                      key={`label-${row}`}
                      x={20 + count * 30 + 5}
                      y={20 + row * 30 + 17}
                      fontSize={14}
                      fill="#333"
                    >
                      {count}
                    </text>
                  ))}
                </svg>
              </div>
              <p style={{ fontStyle: "italic", fontSize: 26 }}>
                Partial sum after 4 rows: 1 + 2 + 3 + 4 = <strong>10</strong> blocks.
              </p>
              <p>
                Extending this idea, the sum of the first <em>n</em> terms of an arithmetic sequence
                (the arithmetic series) can be calculated efficiently rather than adding each term one by one.
              </p>
            </>
          )
        },
    ],
  
    // -----------------------------------------------------
    // CHAPTER 7: Systems of Linear Equations
    // -----------------------------------------------------
    7: [
      {
          title: <h1>Systems of Linear Equations — Slide 1</h1>,
          body: (
            <>
              <p>
                <strong>Definition.</strong> A system of linear equations is two (or more) linear
                equations considered together. The solution is the point(s) where all graphs intersect.
              </p>
              <div style={{ textAlign: "center", margin: "1em 0" }}>
                <svg width={300} height={300} style={{ border: "1px solid #ccc" }}>
                  {/* Axes */}
                  <line x1={150} y1={0}   x2={150} y2={300} stroke="#999" />
                  <line x1={0}   y1={150} x2={300} y2={150} stroke="#999" />
                  {/* Line 1: y = 0.5x + 20 */}
                  <line
                    x1={0}
                    y1={150 - (0.5 * (0 - 150) + 20)}
                    x2={300}
                    y2={150 - (0.5 * (300 - 150) + 20)}
                    stroke="#e53935"
                    strokeWidth={2}
                  />
                  {/* Line 2: y = -x + 100 */}
                  <line
                    x1={0}
                    y1={150 - (-1 * (0 - 150) + 100)}
                    x2={300}
                    y2={150 - (-1 * (300 - 150) + 100)}
                    stroke="#1e88e5"
                    strokeWidth={2}
                  />
                  {/* Intersection marker (approx at x=60,y=50) */}
                  <circle cx={150 + 60} cy={150 - 50} r={4} fill="#43a047" />
                  <text x={150 + 60 + 5} y={150 - 50 - 5} fontSize={12} fill="#333">
                    (60, 50)
                  </text>
                </svg>
              </div>
              <p style={{ fontStyle: "italic", fontSize: 26 }}>
                These two lines intersect at roughly <strong>(60, 50)</strong>; that point is the solution to the system.
              </p>
            </>
          )
        },
        {
          title: <h1>Systems of Linear Equations — Slide 2</h1>,
          body: (
            <>
              <p>
                <strong>Solving by Graphing.</strong> Plot both lines and observe:
              </p>
              <svg
                width="600"
                height="180"
                viewBox="0 0 600 180"
                style={{ border: "1px solid #ccc", display: "block", margin: "0 auto" }}
              >
                {/* Panel 1: one solution */}
                <g transform="translate(0,0)">
                  <rect x={0} y={0} width={200} height={180} fill="#f0f4c3" />
                  {/* Line A */}
                  <line x1={20} y1={160} x2={180} y2={20} stroke="#2e7d32" strokeWidth="2" />
                  {/* Line B */}
                  <line x1={20} y1={60} x2={180} y2={180} stroke="#1565c0" strokeWidth="2" />
                  <circle cx={100} cy={90} r={4} fill="#dd2c00" />
                  <text x={60} y={30} fontSize="12" fill="#333">One solution</text>
                </g>
        
                {/* Panel 2: no solution */}
                <g transform="translate(200,0)">
                  <rect x={0} y={0} width={200} height={180} fill="#ffcdd2" />
                  {/* Parallel Line A */}
                  <line x1={20} y1={160} x2={180} y2={20} stroke="#2e7d32" strokeWidth="2" />
                  {/* Parallel Line B */}
                  <line x1={20} y1={140} x2={180} y2={0}  stroke="#1565c0" strokeWidth="2" />
                  <text x={50} y={30} fontSize="12" fill="#333">No solution</text>
                </g>
        
                {/* Panel 3: infinitely many */}
                <g transform="translate(400,0)">
                  <rect x={0} y={0} width={200} height={180} fill="#e1bee7" />
                  {/* Coincident Line */}
                  <line x1={20} y1={160} x2={180} y2={20} stroke="#2e7d32" strokeWidth="3" />
                  <line x1={20} y1={160} x2={180} y2={20} stroke="#9c27b0" strokeWidth="1" strokeDasharray="4" />
                  <text x={40} y={30} fontSize="12" fill="#333">Infinitely many</text>
                </g>
              </svg>
              <p style={{ textAlign: "center", fontSize: "1em", marginTop: "0.5em" }}>
                <em>Left:</em> lines intersect once &rarr; one solution.&nbsp; 
                <em>Middle:</em> parallel &rarr; no solution.&nbsp; 
                <em>Right:</em> coincident &rarr; infinitely many solutions.
              </p>
            </>
          )
        },
        {
          title: <h1>Systems of Linear Equations — Slide 3</h1>,
          body: (
            <>
              <p>
                <strong>Substitution Method.</strong> First solve one equation for one variable (e.g.
                <code>y = 2x + 1</code>), then substitute that expression into the other equation
                to get a single‐variable equation. Once you solve that, back-substitute to find the
                remaining variable.
              </p>
              <p>
                <em>Try it yourself below:</em>
              </p>
              <SystemSolverTool mode="substitution" />
            </>
          )
        },
        {
          title: <h1>Systems of Linear Equations — Slide 4</h1>,
          body: (
            <>
              <p>
                <strong>Elimination Method (Linear Combination).</strong> The goal is to cancel one variable by adding or subtracting two equations.  
                <br/>
                <em>Example:</em>  
                <code>2x + 3y = 7</code>  
                <code>−2x + 5y = 1</code>  
                Adding them removes <code>x</code> and leaves <code>8y = 8</code>.
              </p>
              <ol>
                <li>Decide which variable to eliminate.</li>
                <li>Multiply one or both equations so those coefficients match.</li>
                <li>Add or subtract to cancel that variable.</li>
                <li>Solve the single-variable equation.</li>
                <li>Back-substitute to find the other variable.</li>
              </ol>
              <p>
                <em>Try it yourself using elimination:</em>
              </p>
              <SystemSolverTool mode="elimination" />
            </>
          )
        },
        {
          title: <h1>Systems of Linear Equations — Slide 5</h1>,
          body: (
            <>
              <p>
                <strong>Applications.</strong> Many real-world problems lead to systems of equations
                (e.g., cost &amp; item number, mixture problems, ticket pricing, etc.).  You translate
                each condition into an equation, then solve the pair.
              </p>
              <p><em>Example:</em></p>
              <pre style={{ background: "#f5f5f5", padding: "0.5em", fontFamily: "monospace" }}>
        3x + 2y = 11  
        2x +  y =  7
              </pre>
              <p>Graphically, each equation is one line—where they intersect is your solution:</p>
              <SystemSolverTool
                mode="application"
                showGraph={true}
                width={400}
                height={250}
              />
              <ul style={{ marginTop: "0.5em", fontSize: "0.9em" }}>
                <li><span style={{ color: "red", fontWeight: "bold" }}>Red line</span> = <code>3x + 2y = 11</code></li>
                <li><span style={{ color: "blue", fontWeight: "bold" }}>Blue line</span> = <code>2x + y = 7</code></li>
              </ul>
            </>
          )
        } 
    ],
  
    // -----------------------------------------------------
    // CHAPTER 8: Right Triangle Trigonometry
    // -----------------------------------------------------
    // -----------------------------------------------------
  // CHAPTER 8: Right Triangle Trigonometry
  // -----------------------------------------------------
  8: [
      {
        title: <h1>Right Triangle Trigonometry — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>Primary Trig Ratios.</strong> For an acute angle <code>θ</code> in a right
              triangle, <code>sin(θ) = opposite/hypotenuse</code>, 
              <code>cos(θ) = adjacent/hypotenuse</code>, 
              <code>tan(θ) = opposite/adjacent</code>.
            </p>
            {/* SVG unit-​triangle showing opp, adj, hyp */}
            <svg
    width="600"
    height="580"
    viewBox="0 0 240 180"
    style={{ border: "1px solid #ccc", margin: "1em 0" }}
  >
    {/* Triangle edges */}
    <line x1="20" y1="160" x2="200" y2="160" stroke="#00796b" strokeWidth="2" />   {/* adjacent */}
    <line x1="200" y1="160" x2="200" y2="60"  stroke="#00796b" strokeWidth="2" />   {/* opposite */}
    <line x1="20" y1="160" x2="200" y2="60"  stroke="#c62828" strokeWidth="2" />   {/* hypotenuse */}
  
    {/* Labels */}
    <text x="110" y="175" fontSize="14" fill="#004d40" textAnchor="middle">
      adjacent
    </text>
    <text
      x="210"
      y="110"
      fontSize="14"
      fill="#004d40"
      textAnchor="middle"
      transform="rotate(90 210,110)"
    >
      opposite
    </text>
    {/* rotate the hypotenuse label around its midpoint (110,110) by –30° */}
    <text
      x="110"
      y="100"
      fontSize="14"
      fill="#c62828"
      textAnchor="middle"
      transform="rotate(-30 110,100)"
    >
      hypotenuse
    </text>
  
    {/* Acute‐angle arc (~29°) at the lower‐left corner (20,160) */}
    <path
      d="M60,160 A40,40 0 0,0 55,140.6"
      fill="none"
      stroke="#000"
      strokeWidth="1.5"
    />
    <text x="45" y="158" fontSize="16" fontWeight="bold">
      θ
    </text>
  </svg>
          </>
        )
      },
      // Slide 2: Finding Sides
      {
          title: <h1>Right Triangle Trigonometry — Slide 2</h1>,
          body: (
            <>
              <p>
                <strong>Finding Sides.</strong> If you know an angle and one side, use the
                appropriate ratio. E.g., for a 30° angle and hypotenuse 10 m:
                <code>sin(30°) = opposite/10</code> implies <code>opp = 10·sin(30°) = 5</code>.
              </p>
              {/* Slide 2 — Finding Sides */}
  <svg
    width="600"
    height="580"
    viewBox="0 0 240 180"
    style={{ margin: "1em 0", border: "1px solid #ccc" }}
  >
    {/* Adjacent (base) */}
    <line
      x1="20"
      y1="160"
      x2="200"
      y2="160"
      stroke="#2e7d32"
      strokeWidth="3"
    />
    {/* Opposite (vertical) */}
    <line
      x1="200"
      y1="60"
      x2="200"
      y2="160"
      stroke="#2e7d32"
      strokeWidth="3"
    />
    {/* Hypotenuse */}
    <line
      x1="20"
      y1="160"
      x2="200"
      y2="60"
      stroke="#c62828"
      strokeWidth="3"
    />
  
    {/* Interior angle arc — always 30° at the corner */}
    <path
      d="M50,160 A30,30 0 0,0 50,140"
      fill="none"
      stroke="#000"
      strokeWidth="1.5"
    />
    <text
      x="55"
      y="152"
      fontSize="16"
      fontWeight="bold"
    >
      30°
    </text>
  
    {/* Labels */}
    <text
      x="110"
      y="172"
      fontSize="14"
      textAnchor="middle"
      fill="#2e7d32"
    >
      10 m (hyp)
    </text>
    <text
      x="205"
      y="110"
      fontSize="14"
      fill="#c62828"
      textAnchor="start"
    >
      5 m
    </text>
  </svg>
  
            </>
          )
        },
        
    
    // Slide 3: Finding Angles
    {
      title: <h1>Right Triangle Trigonometry — Slide 3</h1>,
      body: (
        <>
          <p>
            <strong>Finding Angles.</strong> When two sides of a right triangle are known but
            the angle isn’t, use an inverse trig ratio:
          </p>
          <ul>
            <li><code>θ = sin⁻¹(opposite / hypotenuse)</code></li>
            <li><code>θ = cos⁻¹(adjacent / hypotenuse)</code></li>
            <li><code>θ = tan⁻¹(opposite / adjacent)</code></li>
          </ul>
          <p>
            <strong>Example:</strong> A ladder leans against a wall so that its base is 
            <strong>4 m</strong> from the wall and it reaches <strong>6 m</strong> up.
            Find the angle <code>θ</code> between ladder and ground.
          </p>
    
          <svg
    width="600"
    height="500"
    viewBox="0 0 260 180"
    style={{ margin: "1em 0", border: "1px solid #ccc" }}
  >
    {/* Base (4 m adjacent) */}
    <line x1="20" y1="150" x2="100" y2="150"
          stroke="#2e7d32" strokeWidth="4" />
    <text x="60" y="165" fontSize="14" fill="#2e7d32" textAnchor="middle">
      4 m (adjacent)
    </text>
  
    {/* Vertical (6 m opposite) */}
    <line x1="100" y1="150" x2="100" y2="30"
          stroke="#c62828" strokeWidth="4" />
    <text x="105" y="90" fontSize="14" fill="#c62828">
      6 m (opposite)
    </text>
  
    {/* Hypotenuse */}
    <line x1="20" y1="150" x2="100" y2="30"
          stroke="#1976d2" strokeWidth="4" />
  
    {/*
      Angle arc:
      - M25,150  moves the “pen” slightly right of (20,150).
      - A10,10 0 0,1 29,144 draws a small arc of radius=10,
        sweeping up to (29,144), which is near the corner
        on the diagonal.  The 'sweepFlag=1' ensures it bows inward.
    */}
    <path
    d="
      M50,160          /* Move pen just at/near the lower-left corner */
      A20,20 0 0,0     /* Arc with a smaller radius: 20px */
      55,145           /* End point a bit to the right and up */
    "
    fill="none"
    stroke="#000"
    strokeWidth="1.5"
  />
  
    {/* Angle label */}
    <text x="30" y="148"
          fontSize="16" fontWeight="bold">
      θ
    </text>
  </svg>
          <p>
            <strong>Solution:</strong><br/>
            <code>θ = tan⁻¹(6 / 4) ≈ 56.3°</code>.
          </p>
          <p>
            <em>Check your work</em> by using the other inverses:
            <code>sin⁻¹(6 / √(4²+6²))</code> or
            <code>cos⁻¹(4 / √(4²+6²))</code>—they should all give the same angle!
          </p>
        </>
      )
    },
    
    // Slide 4: Applications
    {
      title: <h1>Right Triangle Trigonometry — Slide 4</h1>,
      body: (
        <>
          <p>
            <strong>Applications.</strong> Right-triangle trig pops up everywhere:
          </p>
          <ul>
            <li>
              <strong>Surveying &amp; Construction:</strong> Estimate heights from ground-level
              angles and distances.
            </li>
            <li>
              <strong>Ramp &amp; Roof Design:</strong> Compute rise/run for ramps, rafters or slopes
              to meet codes.
            </li>
            <li>
              <strong>Navigation &amp; Aviation:</strong> Find bearing angles or glide slopes when
              direct measurement isn’t possible.
            </li>
          </ul>
          <p>
            In practice you often <em>combine</em>:
          </p>
          <ol>
            <li>
              <strong>Pythagorean theorem</strong> (<code>a² + b² = c²</code>)
            </li>
            <li>
              then a <strong>trig ratio</strong> (<code>θ = sin⁻¹, cos⁻¹, tan⁻¹</code>) 
              to find the unknown side or angle.
            </li>
          </ol>
          <p>
            <strong>Surveyor Scenario:</strong> From distance <code>d</code> you measure an
            elevation angle <code>θ</code> up to the top of a structure. Find its height <code>h</code>.
          </p>
    
          <svg
    width="600"
    height="500"
    viewBox="0 0 300 200"
    style={{
      margin: "1em 0",
      border: "1px solid #ccc",
      background: "#fff"
    }}
  >
    {/* Ground line */}
    <line x1="20" y1="160" x2="280" y2="160" stroke="#555" strokeWidth="2" />
  
    {/* Building (opposite) */}
    <line x1="180" y1="160" x2="180" y2="80" stroke="#2e7d32" strokeWidth="4" />
  
    {/* Line of sight (hypotenuse) */}
    <line x1="20" y1="160" x2="180" y2="80" stroke="#c62828" strokeWidth="4" />
  
    {/*
      Angle arc, previously "M15,160 A15,15 0 0,0 4,147".
      We move both points ~+10 px in x so it sits closer to (20,160):
        - Start: (15 -> 25)
        - End:   ( 4 -> 14)
      Keeping the same radius (15,15) and flags (0 0,0).
    */}
    <path
      d="M55,160 A15,15 0 0,0 50,147"
      fill="none"
      stroke="#000"
      strokeWidth="1.5"
    />
    {/* Angle label θ, nudged to follow the arc */}
    <text x="40" y="159" fontSize="12" fontWeight="bold">
      θ
    </text>
  
    {/* Distance label (d) */}
    <text
      x={(20 + 180) / 2}
      y={175}
      fontSize="14"
      fill="#333"
      textAnchor="middle"
    >
      d (distance)
    </text>
  
    {/* Height label (h) */}
    <text
      x={185}
      y={(80 + 160) / 2}
      fontSize="14"
      fill="#2e7d32"
      textAnchor="start"
    >
      h (height)
    </text>
  </svg>
          <p>
            <strong>How to solve:</strong><br />
            From <code>tan(θ) = h / d</code> → <code>h = d · tan(θ)</code>.<br />
            (And you can always check with <code>a² + b² = c²</code> on the hypotenuse.)
          </p>
        </>
      )
    },
    
    // Slide 5: SOH-CAH-TOA Mnemonic
    {
      title: <h1>Right Triangle Trigonometry — Slide 5</h1>,
      body: (
        <>
          <p>
            That’s the core of right‐triangle trig: 
            <code>SOH‐CAH‐TOA</code> helps you remember the definitions of
            sine, cosine, and tangent:
          </p>
    
          <svg
    width="500"
    height="120"
    style={{ margin: "1em 0", background: "#fff" }}
  >
    {/* SOH row */}
    <text x="10" y="25" fontSize="26" fontWeight="bold">
      SOH
    </text>
    <text x="75" y="25" fontSize="26">
      = Sin = Opposite / Hypotenuse
    </text>
  
    {/* CAH row */}
    <text x="10" y="55" fontSize="26" fontWeight="bold">
      CAH
    </text>
    <text x="75" y="55" fontSize="26">
      = Cos = Adjacent / Hypotenuse
    </text>
  
    {/* TOA row */}
    <text x="10" y="85" fontSize="26" fontWeight="bold">
      TOA
    </text>
    <text x="75" y="85" fontSize="26">
      = Tan = Opposite / Adjacent
    </text>
  </svg>
  
    
          <p>
            By labeling a right triangle’s sides relative to an angle <code>θ</code>:
            <ul>
              <li>
                <strong>Opposite</strong> side is across from angle <code>θ</code>.
              </li>
              <li>
                <strong>Adjacent</strong> side is alongside <code>θ</code>, but not the hypotenuse.
              </li>
              <li>
                <strong>Hypotenuse</strong> is always the longest side, across from the 90° angle.
              </li>
            </ul>
            Once you identify which side is opp/adj/hyp, use these 
            ratios to solve for missing sides or angles.
          </p>
    
          {/* Tiny “SOH-CAH-TOA” diagram */}
          <svg
    width="650"
    height="650"
    viewBox="0 0 160 230"
    style={{
      margin: "1em 0",
      border: "1px solid #ccc",
      background: "#fff"
    }}
  >
    {/* Base (adjacent) */}
    <line x1="20" y1="150" x2="100" y2="150" stroke="#2e7d32" strokeWidth="4" />
    <text
      x={(20 + 100) / 2}
      y={165}
      fontSize="14"
      fill="#2e7d32"
      textAnchor="middle"
    >
      adjacent
    </text>
  
    {/* Vertical (opposite) */}
    <line x1="100" y1="150" x2="100" y2="80" stroke="#c62828" strokeWidth="4" />
    <text x="105" y={(150 + 80) / 2} fontSize="14" fill="#c62828">
      opposite
    </text>
  
    {/* Hypotenuse (blue) */}
    <line x1="20" y1="150" x2="100" y2="80" stroke="#1976d2" strokeWidth="4" />
    <text
      x={(20 + 40) / 2}
      y={(150 + 60) / 2 - 5}
      fontSize="14"
      fill="#1976d2"
      textAnchor="middle"
    >
      hypotenuse
    </text>
  
    {/* Angle arc in lower-left corner (inward curve) */}
    <path
      /*
        Moves slightly right of (20,150) then
        arcs up/left toward the diagonal.
        The flags (0 0,1) create a small, clockwise arc.
      */
      d="M45,150 A15,12 0 0,0 42,130"
      fill="none"
      stroke="#000"
      strokeWidth="1.5"
    />
    <text x="35" y="147" fontSize="16" fontWeight="bold">
      θ
    </text>
  </svg>
  
    
          <p>
            Practice by drawing a few triangles and labeling each side. Then apply
            <code>sin(θ)</code>, <code>cos(θ)</code>, or <code>tan(θ)</code> to find unknowns.
          </p>
        </>
      )
    },
    ],
    
  
    // -----------------------------------------------------
    // CHAPTER 9: Financial Literacy (Income, Gross/Net Pay)
    // -----------------------------------------------------
    9: [
      {
        title: <h1>Financial Literacy — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>Types of Income.</strong> There are four common ways people earn money:
            </p>
            <ul>
              <li><strong>Hourly wage:</strong> Paid by the hour (e.g. $16/hr).</li>
              <li><strong>Salary:</strong> Fixed annual amount, regardless of hours worked.</li>
              <li><strong>Commission:</strong> A percentage of total sales (e.g. 5 % of sales).</li>
              <li><strong>Piecework:</strong> Paid per item produced (e.g. $2 per widget).</li>
            </ul>
          </>
        )
      },
      {
        title: <h1>Financial Literacy — Slide 2</h1>,
        body: (
          <>
            <p>
              <strong>Gross Pay Calculation.</strong><br />
              <em>Hourly:</em> Rate × Hours worked<br />
              <em>Commission:</em> Sales × Commission %<br />
              <em>Salary:</em> Annual salary ÷ Number of pay periods
            </p>
          </>
        )
      },
      {
        title: <h1>Financial Literacy — Slide 3</h1>,
        body: (
          <>
            <p>
              <strong>Deductions & Net Income.</strong><br />
              Net pay = Gross pay − (Taxes + EI + CPP + Other deductions).<br />
              <em>In class we often assume a flat 20 % tax, so:</em><br />
              Net = Gross × 0.80
            </p>
          </>
        )
      },
      {
        title: <h1>Financial Literacy — Slide 4</h1>,
        body: (
          <>
            <p>
              <strong>Basic Income Tax.</strong> Real taxes use brackets. A sample simplified table:
            </p>
            <table style={{ width: "100%", borderCollapse: "collapse", margin: "1em 0" }}>
              <thead>
                <tr>
                  <th style={{ border: "1px solid #ccc", padding: 6 }}>Bracket</th>
                  <th style={{ border: "1px solid #ccc", padding: 6 }}>Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>$0 – $10 000</td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>10 %</td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>$10 001 – $50 000</td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>20 %</td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>Over $50 000</td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>30 %</td>
                </tr>
              </tbody>
            </table>
            <p>
              For our purposes, you can assume a flat rate (e.g. 20 %) and compute:
              <code> net = gross × 0.80</code>.
            </p>
          </>
        )
      },
      {
        title: <h1>Financial Literacy — Slide 5</h1>,
        body: (
          <>
            <p>
              That wraps up basic personal finance in Grade 10! Next: a quick summary and your final 50-question quiz.
            </p>
          </>
        )
      }
    ],
  
    // -----------------------------------------------------
    // CHAPTER 10: Summary & Final Quiz
    // -----------------------------------------------------
    10: [
      {
        title: <h1>Grade 10 Summary</h1>,
        body: (
          <>
            <p>
              You’ve covered: Exponents & Radicals, Polynomial Operations, Polynomial Factoring,
              Relations & Functions, Linear Functions, Arithmetic Sequences, Systems of Linear
              Equations, Right-Triangle Trigonometry, and Financial Literacy. Well done!
            </p>
            <p>
              Now you’re ready for the final 50-question quiz covering everything. Good luck!
            </p>
          </>
        )
      }
    ]
  };